import {
    Button,
    Col,
    FormGroup,
    Label,
    Nav,
    NavItem,
    NavLink,
    Popover,
    PopoverBody,
    Row,
    TabContent,
    TabPane,
} from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import { ListItem } from "../app/types";
import DateSelect2 from "./DateSelect2";
import { dateHelpers } from "../app/helpers";
import { storageHelper, storageKeys } from "../app/storage-helper";
import { useAppSelector } from "../app/hooks";
import { selectAuthUser } from "../features/auth/auth.slice";
import SapIcon from "./SapIcon";

const MODE_RELATIVE = "relative";
const MODE_CUSTOM = "custom";
const MODE_MONTH = "month";

const RELATIVE_DURATIONS: ListItem[] = [
    { key: "today", value: "Today" },
    { key: "yesterday", value: "Yesterday" },
    { key: "last_7_days", value: "Last 7d" },
    { key: "last_30_days", value: "Last 30d" },
    { key: "this_week", value: "This Week" },
    { key: "last_week", value: "Last Week" },
    { key: "this_month", value: "This Month" },
    { key: "last_month", value: "Last Month" },
    { key: "this_year", value: "This Year" },
    { key: "last_year", value: "Last Year" },
    { key: "all", value: "All" },
];

interface DateRangeStorageItem {
    mode: "custom" | "relative" | "month";
    relativeRange?: string;
    customRange?: DateRange;
    definedRangeStartDate?: string;
}

interface CachedDateRangeStorageItem {
    entry: DateRangeStorageItem;
    dateRange: DateRange;
}

export interface DateRange {
    from?: string;
    to?: string;
}

interface Props {
    id: string;
    defaultValue?:
        | "today"
        | "yesterday"
        | "last_7_days"
        | "last_30_days"
        | "this_week"
        | "last_week"
        | "this_month"
        | "last_month"
        | "this_year"
        | "last_year";
    onChange: (range: DateRange) => void;
    exclude?: string[];
    className?: string;
}

function getMonthDateRange(month: string): DateRange {
    const startDate = new Date(month);
    const fromDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const toDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

    return {
        from: dateHelpers.toIsoString(fromDate),
        to: dateHelpers.toIsoString(toDate),
    };
}

function getDefinedRangeDisplayText(startDate: string) {
    return dateHelpers.formatDate(startDate, "MMM YYYY");
}

function getRelativeDateRange(range: string): DateRange {
    let fromDate: Date | undefined;
    let toDate: Date | undefined;

    const date = new Date();

    switch (range) {
        case "today": {
            fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            break;
        }
        case "yesterday": {
            fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            break;
        }
        case "last_7_days": {
            fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6);
            toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            break;
        }
        case "last_30_days": {
            fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 29);
            toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            break;
        }
        case "this_week": {
            const firstDayOfWeek = dateHelpers.getFirstDayOfWeek(date);
            fromDate = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), firstDayOfWeek.getDate());
            toDate = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), firstDayOfWeek.getDate() + 6);
            break;
        }
        case "last_week": {
            const firstDayOfLastWeek = dateHelpers.getFirstDayOfWeek(
                new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7),
            );
            fromDate = new Date(
                firstDayOfLastWeek.getFullYear(),
                firstDayOfLastWeek.getMonth(),
                firstDayOfLastWeek.getDate(),
            );
            toDate = new Date(
                firstDayOfLastWeek.getFullYear(),
                firstDayOfLastWeek.getMonth(),
                firstDayOfLastWeek.getDate() + 6,
            );
            break;
        }
        case "this_month": {
            fromDate = new Date(date.getFullYear(), date.getMonth(), 1);
            toDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            break;
        }
        case "last_month": {
            fromDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            toDate = new Date(date.getFullYear(), date.getMonth(), 0);
            break;
        }
        case "this_year": {
            fromDate = new Date(date.getFullYear(), 0, 1);
            toDate = new Date(date.getFullYear(), 11, 31);
            break;
        }
        case "last_year": {
            fromDate = new Date(date.getFullYear() - 1, 0, 1);
            toDate = new Date(date.getFullYear() - 1, 11, 31);
            break;
        }
    }

    return {
        from: fromDate && dateHelpers.toIsoString(fromDate),
        to: toDate && dateHelpers.toIsoString(toDate),
    };
}

export function getCachedDateRange(smartDateRangeCtrlId: string, defaultValue?: string) {
    const tenantId = storageHelper.getValue(storageKeys.tenantId) || "";
    if (tenantId) {
        const storageKey = `${tenantId}_${smartDateRangeCtrlId}`;
        const cachedFilter = getSmartDateRangeCached(storageKey);

        if (cachedFilter) {
            const entry = cachedFilter.entry;

            if (entry.relativeRange) return getRelativeDateRange(entry.relativeRange);

            if (entry.customRange && Object.keys(entry.customRange).length > 0) return entry.customRange;

            if (entry.definedRangeStartDate) return getMonthDateRange(entry.definedRangeStartDate);
        } else if (defaultValue) {
            return getRelativeDateRange(defaultValue);
        }
    }
    return undefined;
}

export function getSmartDateRangeCached(storageKey: string) {
    const savedFilter = storageHelper.getValue(storageKey);
    if (savedFilter) {
        const entry = JSON.parse(savedFilter) as DateRangeStorageItem;

        const dateRange =
            entry.mode == MODE_RELATIVE
                ? getRelativeDateRange(entry.relativeRange!)
                : entry.mode == MODE_MONTH && entry.definedRangeStartDate
                  ? getMonthDateRange(entry.definedRangeStartDate)
                  : entry.customRange || {};

        const cachedFilter: CachedDateRangeStorageItem = { entry, dateRange };

        return cachedFilter;
    }

    return undefined;
}

export default function SmartDateRangeSelector({ id, onChange, defaultValue, exclude, className }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const [activeMode, setActiveMode] = useState<string>();
    const [relativeRange, setRelativeRange] = useState<string>();
    const [customDateRange, setCustomDateRange] = useState<DateRange>({});
    const [definedRangeStartDate, setDefinedRangeStartDate] = useState<string>();

    const user = useAppSelector(selectAuthUser);

    const storageKey = useMemo(() => {
        if (user?.tenantId) {
            return `${user?.tenantId}_${id}`;
        }
    }, [user?.tenantId]);

    useEffect(() => {
        const savedFilter = storageKey ? getSmartDateRangeCached(storageKey) : undefined;
        if (savedFilter) {
            const entry = savedFilter.entry;

            setActiveMode(entry.mode);
            entry.relativeRange && setRelativeRange(entry.relativeRange);
            entry.customRange && setCustomDateRange({ ...entry.customRange });
            entry.definedRangeStartDate && setDefinedRangeStartDate(entry.definedRangeStartDate);

            onChange(savedFilter.dateRange);
        } else {
            setActiveMode(MODE_RELATIVE);
            setRelativeRange(defaultValue || "all");

            onChange(getRelativeDateRange(defaultValue || "all"));
        }
    }, [storageKey]);

    const toggle = () => setIsOpen(!isOpen);

    const handleRelativeSelect = (range: string) => {
        setRelativeRange(range);
        setCustomDateRange({});
        setDefinedRangeStartDate(undefined);

        saveInStorage(MODE_RELATIVE, range);

        const dateRange = getRelativeDateRange(range);
        onChange(dateRange);

        toggle();
    };

    const handleCustomRangeSelect = () => {
        setRelativeRange(undefined);
        setDefinedRangeStartDate(undefined);

        saveInStorage(MODE_CUSTOM, undefined, customDateRange || {});

        onChange(customDateRange || {});

        toggle();
    };

    const handleMonthSelect = (date?: string, preventToggle?: boolean) => {
        setDefinedRangeStartDate(date);
        setRelativeRange(undefined);
        setCustomDateRange({});

        saveInStorage(date ? MODE_MONTH : MODE_CUSTOM, undefined, {}, date);

        onChange((date && getMonthDateRange(date)) || {});

        !preventToggle && toggle();
    };

    const saveInStorage = (
        mode: "custom" | "relative" | "month",
        relativeRange?: string,
        customRange?: DateRange,
        definedRangeStartDate?: string,
    ) => {
        const entry: DateRangeStorageItem = {
            mode,
            relativeRange,
            customRange,
            definedRangeStartDate,
        };

        storageKey && storageHelper.setValue(storageKey, JSON.stringify(entry));
    };

    const traverseMonthForward = () => traverseMonth(1);

    const traverseMonthBackward = () => traverseMonth(-1);

    const traverseMonth = (increment: number) => {
        if (definedRangeStartDate) {
            const date = new Date(definedRangeStartDate);
            const newDate = new Date(date.getFullYear(), date.getMonth() + increment, 1);
            const newDateStr = dateHelpers.toIsoString(newDate);

            handleMonthSelect(newDateStr, true);
        }
    };

    const isFixedRelativeRange = definedRangeStartDate;

    return (
        <>
            <div className={`text-nowrap ${className || ""}`} style={{ cursor: "pointer" }}>
                {isFixedRelativeRange && (
                    <SapIcon icon="chevron-left" mode="button" className="ms-2" onClick={traverseMonthBackward} />
                )}
                <span id="date_range_popover_div" className="text-nowrap">
                    <SapIcon icon="calendar-days" mode="button" className="me-1" />
                    <u>
                        {relativeRange
                            ? RELATIVE_DURATIONS.find((d) => d.key == relativeRange)?.value
                            : definedRangeStartDate
                              ? getDefinedRangeDisplayText(definedRangeStartDate)
                              : `${
                                    (customDateRange.from && dateHelpers.toShortDateString(customDateRange.from)) || "*"
                                }-${(customDateRange.to && dateHelpers.toShortDateString(customDateRange.to)) || "*"}`}
                    </u>
                </span>
                {isFixedRelativeRange && (
                    <SapIcon icon="chevron-right" mode="button" className="ms-1" onClick={traverseMonthForward} />
                )}
            </div>
            <Popover
                isOpen={isOpen}
                placement="bottom"
                target="date_range_popover_div"
                toggle={toggle}
                trigger="legacy"
            >
                <PopoverBody>
                    <Nav tabs>
                        {!exclude?.includes("relative") && (
                            <NavItem>
                                <NavLink
                                    className={activeMode == MODE_RELATIVE ? "active" : ""}
                                    onClick={() => setActiveMode(MODE_RELATIVE)}
                                >
                                    Relative
                                </NavLink>
                            </NavItem>
                        )}
                        <NavItem>
                            <NavLink
                                className={activeMode == MODE_MONTH ? "active" : ""}
                                onClick={() => setActiveMode(MODE_MONTH)}
                            >
                                Month
                            </NavLink>
                        </NavItem>

                        {!exclude?.includes("custom") && (
                            <NavItem>
                                <NavLink
                                    className={activeMode == MODE_CUSTOM ? "active" : ""}
                                    onClick={() => setActiveMode(MODE_CUSTOM)}
                                >
                                    Custom
                                </NavLink>
                            </NavItem>
                        )}
                    </Nav>
                    <TabContent activeTab={activeMode} style={{ width: "200px" }}>
                        <TabPane tabId={MODE_RELATIVE} className="p-2">
                            <Row>
                                {/* <Col sm="12"> */}
                                {RELATIVE_DURATIONS.filter((d) => !exclude?.includes(d.key)).map((d) => (
                                    <Col key={d.key} xs={6} className="text-start p-1">
                                        <a
                                            href="#"
                                            style={{ textDecoration: "none" }}
                                            onClick={() => handleRelativeSelect(d.key)}
                                        >
                                            {d.value}
                                            <span>{d.key == relativeRange ? " *" : ""}</span>
                                        </a>
                                    </Col>
                                ))}
                                {/* </Col> */}
                            </Row>
                        </TabPane>
                        <TabPane tabId={MODE_MONTH}>
                            <Row>
                                <Col sm="12" className="pt-3">
                                    <FormGroup>
                                        <DateSelect2
                                            dateFormat="MMM yyyy"
                                            value={definedRangeStartDate}
                                            onChange={handleMonthSelect}
                                            monthPicker
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </TabPane>
                        <TabPane tabId={MODE_CUSTOM}>
                            <Row className="pt-3">
                                <Col sm="12">
                                    <FormGroup>
                                        <Label>From</Label>
                                        <Col>
                                            <DateSelect2
                                                value={customDateRange?.from}
                                                onChange={(from) =>
                                                    setCustomDateRange({ from, to: customDateRange.to || from })
                                                }
                                                isClearable
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>To</Label>
                                        <DateSelect2
                                            value={customDateRange?.to}
                                            onChange={(to) =>
                                                setCustomDateRange({ from: customDateRange.from || to, to })
                                            }
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button color="primary" size="sm" onClick={handleCustomRangeSelect}>
                                        Apply
                                    </Button>
                                </Col>
                            </Row>
                        </TabPane>
                    </TabContent>
                </PopoverBody>
            </Popover>
        </>
    );
}
